import { useMemo, useState, type ReactNode } from "react"
import {
  AlertCircle,
  BadgeCheck,
  ChevronLeft,
  Database,
  KeyRound,
  Link2,
  Plus,
  Search,
  Shield,
  Sparkles,
  Unplug,
  XCircle,
} from "lucide-react"
import { Input } from "../../internal/primitives/input"
import { Label } from "../../internal/primitives/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../internal/primitives/select"
import { cn } from "../../lib/utils"
import { MapInteractivityModalShell } from "../map-interactivity/MapInteractivityModalShell"
import type {
  LayerExperience,
  LayerExperienceResolverConnectionDefinition,
  LayerExperienceResolverAvailability,
  LayerExperienceResolverDefinition,
  LayerExperienceResolverRendererKind,
} from "../../types/layer-experience"

export interface DataSectionProps {
  enrichments?: LayerExperience["enrichments"]
  onUpdate: (patch: Partial<LayerExperience["enrichments"]>) => void
  availableFields: Array<{ name: string; type: string }>
  availableResolverCatalog?: LayerExperienceResolverDefinition[] | null
  availableResolverConnections?: LayerExperienceResolverConnectionDefinition[] | null
}

const FAILURE_POLICY_OPTIONS = [
  { value: "skip_step", label: "Skip Step" },
  { value: "stop_pipeline", label: "Stop Pipeline" },
  { value: "continue_with_empty_result", label: "Continue With Empty Result" },
  { value: "show_runtime_warning", label: "Show Runtime Warning" },
] as const

const RENDERER_LABELS: Record<LayerExperienceResolverRendererKind, string> = {
  record_summary: "Record Summary",
  notes_timeline: "Notes Timeline",
  activity_feed: "Activity Feed",
  address_details: "Address Details",
  related_items_table: "Related Items Table",
}

export function DataSection({
  enrichments,
  onUpdate,
  availableResolverCatalog,
  availableResolverConnections,
}: DataSectionProps) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pendingAttachment, setPendingAttachment] = useState<{
    resolver: LayerExperienceResolverDefinition
    compatibleConnections: LayerExperienceResolverConnectionDefinition[]
  } | null>(null)
  const [search, setSearch] = useState("")

  const attachedResolvers = useMemo(() => enrichments?.resolver_definitions ?? [], [enrichments?.resolver_definitions])
  const currentSourceId = enrichments?.source_definition?.source_id ?? null
  const currentSourceLayer = enrichments?.source_definition?.source_layer ?? null
  const catalog = availableResolverCatalog ?? attachedResolvers
  const connections = useMemo(
    () => availableResolverConnections ?? [],
    [availableResolverConnections],
  )
  const connectionsById = useMemo(
    () => new Map(connections.map(connection => [connection.resolver_connection_id, connection])),
    [connections],
  )

  const attachedKeys = useMemo(
    () => new Set(attachedResolvers.map(resolveResolverIdentity).filter(Boolean)),
    [attachedResolvers],
  )

  const pickerEntries = useMemo(() => {
    const query = search.trim().toLowerCase()
    return catalog
      .map(resolver => {
        const identity = resolveResolverIdentity(resolver)
        const compatibility =
          matchesResolverContext(resolver, currentSourceId, currentSourceLayer) || resolver.availability === "available"
        const alreadyAttached = identity ? attachedKeys.has(identity) : false
        const derivedAvailability: LayerExperienceResolverAvailability =
          resolver.availability ??
          (compatibility ? "available" : "layer_incompatible")
        const disabledReason =
          alreadyAttached
            ? "Already attached to this layer."
            : derivedAvailability !== "available"
              ? resolver.availability_reason || formatAvailabilityReason(derivedAvailability)
              : null
        const haystack = [
          resolver.label,
          resolver.description,
          resolver.provider,
          resolver.resolver_type_id,
          resolver.resolver_id,
          resolver.resolver_connection_id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
        if (query && !haystack.includes(query)) {
          return null
        }
        return {
          resolver,
          identity,
          compatibleConnections: resolveCompatibleResolverConnections(resolver, connections),
          disabled: alreadyAttached || derivedAvailability !== "available",
          disabledReason,
          availability: derivedAvailability,
        }
      })
      .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry))
      .sort((left, right) => {
        if (left.disabled !== right.disabled) {
          return left.disabled ? 1 : -1
        }
        return resolveResolverTitle(left.resolver).localeCompare(resolveResolverTitle(right.resolver))
      })
  }, [attachedKeys, catalog, connections, currentSourceId, currentSourceLayer, search])

  const updateResolvers = (nextResolvers: LayerExperienceResolverDefinition[]) => {
    onUpdate({ resolver_definitions: nextResolvers })
  }

  const updateResolver = (resolverIdentity: string, patch: Partial<LayerExperienceResolverDefinition>) => {
    updateResolvers(
      attachedResolvers.map(resolver =>
        resolveResolverIdentity(resolver) === resolverIdentity ? { ...resolver, ...patch } : resolver,
      ),
    )
  }

  const attachResolver = (
    template: LayerExperienceResolverDefinition,
    selectedConnection?: LayerExperienceResolverConnectionDefinition | null,
    options?: { mode?: "auto" | "managed" },
  ) => {
    const useManagedConnection = options?.mode === "managed"
    const nextAttachmentId = buildResolverAttachmentId(template, attachedResolvers)
    const nextResolver: LayerExperienceResolverDefinition = {
      ...template,
      resolver_attachment_id: nextAttachmentId,
      resolver_type_id: template.resolver_type_id ?? template.resolver_id,
      resolver_connection_id: useManagedConnection
        ? null
        : selectedConnection?.resolver_connection_id ?? template.resolver_connection_id ?? null,
      source_id: currentSourceId,
      source_layer: currentSourceLayer,
      availability: template.availability ?? "available",
      availability_reason: template.availability_reason ?? null,
      connection_status: useManagedConnection
        ? null
        : selectedConnection?.status ?? template.connection_status ?? null,
      renderer_kind: template.renderer_kind ?? template.default_renderer_kind ?? template.supported_renderer_kinds?.[0] ?? null,
    }
    updateResolvers([...attachedResolvers, nextResolver])
    setPickerOpen(false)
    setPendingAttachment(null)
    setSearch("")
  }

  const beginResolverAttach = (
    resolver: LayerExperienceResolverDefinition,
    compatibleConnections: LayerExperienceResolverConnectionDefinition[],
  ) => {
    if (compatibleConnections.length > 1) {
      setPendingAttachment({
        resolver,
        compatibleConnections,
      })
      return
    }
    attachResolver(resolver, compatibleConnections[0] ?? null)
  }

  const removeResolver = (resolverIdentity: string) => {
    updateResolvers(attachedResolvers.filter(resolver => resolveResolverIdentity(resolver) !== resolverIdentity))
  }

  return (
    <>
      <div className="flex flex-col gap-6 pb-10">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-0.5">
            <Label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground/70">
              Attached Resolvers
            </Label>
            <p className="text-[10px] text-muted-foreground">
              Host-approved integrations for this source layer.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setPendingAttachment(null)
              setSearch("")
              setPickerOpen(true)
            }}
            className="inline-flex items-center gap-1 rounded border border-primary/10 px-2 py-1 text-[10px] font-bold text-primary transition-colors hover:bg-primary/5"
          >
            <Plus className="h-3 w-3" />
            Attach Resolver
          </button>
        </div>

        <div className="grid gap-3">
          {attachedResolvers.map(resolver => {
            const resolverIdentity = resolveResolverIdentity(resolver)
            const availability = resolver.availability ?? "available"
            const statusTone = resolveAvailabilityTone(availability)
            const supportedRenderers = resolver.supported_renderer_kinds ?? []
            const requiredInputs = resolver.required_inputs ?? []
            const compatibleConnections = resolveCompatibleResolverConnections(resolver, connections)
            const activeConnection = resolver.resolver_connection_id
              ? connectionsById.get(resolver.resolver_connection_id) ?? null
              : null

            return (
              <div
                key={resolverIdentity}
                className="overflow-hidden rounded-lg border border-border/70 bg-card shadow-sm"
              >
                <div className="flex items-start justify-between gap-4 border-b border-border/60 px-4 py-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[12px] font-bold text-foreground">{resolveResolverTitle(resolver)}</span>
                      <span className={cn("rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider", statusTone.className)}>
                        {statusTone.label}
                      </span>
                      {resolver.provider ? (
                        <span className="rounded border border-border/70 bg-muted/30 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                          {resolver.provider}
                        </span>
                      ) : null}
                      {resolver.renderer_kind ? (
                        <span className="rounded border border-primary/15 bg-primary/5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary">
                          {RENDERER_LABELS[resolver.renderer_kind]}
                        </span>
                      ) : null}
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      {resolver.description || "Resolver metadata is available for this attachment."}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeResolver(resolverIdentity)}
                    className="inline-flex items-center gap-1 rounded border border-destructive/20 px-2 py-1 text-[10px] font-bold text-destructive transition-colors hover:bg-destructive/5"
                  >
                    <XCircle className="h-3 w-3" />
                    Remove
                  </button>
                </div>

                <div className="grid gap-4 px-4 py-4">
                  <div className="grid gap-3 md:grid-cols-2">
                    <MetricRow
                      icon={<Link2 className="h-3 w-3" />}
                      label="Attachment"
                      value={resolver.resolver_attachment_id || "Not assigned"}
                    />
                    <MetricRow
                      icon={<Database className="h-3 w-3" />}
                      label="Resolver"
                      value={resolver.resolver_type_id || resolver.resolver_id}
                    />
                    <MetricRow
                      icon={<KeyRound className="h-3 w-3" />}
                      label="Connection"
                      value={formatResolverConnectionSummary(activeConnection, resolver)}
                    />
                    <MetricRow
                      icon={<AlertCircle className="h-3 w-3" />}
                      label="Failure Policy"
                      value={formatTokenLabel(resolver.failure_policy ?? "continue_with_empty_result")}
                    />
                  </div>

                  {resolver.availability_reason ? (
                    <div className="rounded-lg border border-border/60 bg-muted/10 px-3 py-2 text-[10px] text-muted-foreground">
                      {resolver.availability_reason}
                    </div>
                  ) : null}

                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="grid gap-1.5">
                      <Label className="text-[10px] font-bold uppercase text-muted-foreground/60">Display Label</Label>
                      <Input
                        value={resolver.label ?? ""}
                        onChange={event => updateResolver(resolverIdentity, { label: event.target.value })}
                        className="h-8 text-xs"
                        placeholder="Operator-facing resolver name"
                      />
                    </div>

                    <div className="grid gap-1.5">
                      <Label className="text-[10px] font-bold uppercase text-muted-foreground/60">Renderer</Label>
                      <Select
                        value={resolver.renderer_kind ?? "__none__"}
                        onValueChange={value =>
                          updateResolver(resolverIdentity, {
                            renderer_kind: value === "__none__" ? null : (value as LayerExperienceResolverRendererKind),
                          })
                        }
                      >
                        <SelectTrigger className="h-8 bg-background text-xs">
                          <SelectValue placeholder="No renderer selected" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">No renderer</SelectItem>
                          {supportedRenderers.map(renderer => (
                            <SelectItem key={renderer} value={renderer}>
                              {RENDERER_LABELS[renderer]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid gap-1.5">
                      <Label className="text-[10px] font-bold uppercase text-muted-foreground/60">Connection</Label>
                      <Select
                        value={resolver.resolver_connection_id ?? "__managed__"}
                        onValueChange={value => {
                          const selectedConnection = value === "__managed__" ? null : connectionsById.get(value) ?? null
                          updateResolver(resolverIdentity, {
                            resolver_connection_id: value === "__managed__" ? null : value,
                            connection_status: selectedConnection?.status ?? null,
                          })
                        }}
                      >
                        <SelectTrigger className="h-8 bg-background text-xs">
                          <SelectValue placeholder="Managed by host" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__managed__">Managed by host</SelectItem>
                          {compatibleConnections.map(connection => (
                            <SelectItem key={connection.resolver_connection_id} value={connection.resolver_connection_id}>
                              {formatResolverConnectionOptionLabel(connection)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <ConnectionDetails
                        connection={activeConnection}
                        fallbackStatus={resolver.connection_status}
                        fallbackConnectionId={resolver.resolver_connection_id}
                      />
                    </div>

                    <div className="grid gap-1.5 md:col-span-2">
                      <Label className="text-[10px] font-bold uppercase text-muted-foreground/60">Description</Label>
                      <Input
                        value={resolver.description ?? ""}
                        onChange={event => updateResolver(resolverIdentity, { description: event.target.value })}
                        className="h-8 text-xs"
                        placeholder="What this resolver does and when operators should use it"
                      />
                    </div>

                    <div className="grid gap-1.5">
                      <Label className="text-[10px] font-bold uppercase text-muted-foreground/60">Failure Policy</Label>
                      <Select
                        value={resolver.failure_policy ?? "continue_with_empty_result"}
                        onValueChange={value =>
                          updateResolver(resolverIdentity, {
                            failure_policy: value as LayerExperienceResolverDefinition["failure_policy"],
                          })
                        }
                      >
                        <SelectTrigger className="h-8 bg-background text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FAILURE_POLICY_OPTIONS.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid gap-1.5">
                      <Label className="text-[10px] font-bold uppercase text-muted-foreground/60">Requirements</Label>
                      <div className="flex min-h-8 flex-wrap items-center gap-1 rounded-md border border-input bg-background px-2 py-1">
                        {requiredInputs.length > 0 ? (
                          requiredInputs.map(input => (
                            <span
                              key={input.input_key}
                              className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground"
                            >
                              {input.label || input.input_key}
                            </span>
                          ))
                        ) : (
                          <span className="text-[10px] text-muted-foreground">No required inputs published.</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <TokenGroup
                      icon={<Shield className="h-3 w-3" />}
                      label="Permissions"
                      items={resolver.required_permissions ?? resolver.permissions ?? []}
                      emptyLabel="No extra permissions"
                    />
                    <TokenGroup
                      icon={<Sparkles className="h-3 w-3" />}
                      label="Renderer Support"
                      items={supportedRenderers.map(renderer => RENDERER_LABELS[renderer])}
                      emptyLabel="No renderer profiles"
                    />
                  </div>
                </div>
              </div>
            )
          })}

          {attachedResolvers.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed bg-muted/10 p-8 text-center">
              <Database className="mb-2 h-6 w-6 text-muted-foreground/30" />
              <p className="text-[10px] font-medium italic text-muted-foreground">
                No resolvers are attached to this layer.
              </p>
              <p className="mt-1 text-[10px] text-muted-foreground/70">
                Attach an approved resolver to enable host-managed notes, related records, or address lookups.
              </p>
            </div>
          ) : null}
        </div>
      </div>

      <MapInteractivityModalShell
        open={pickerOpen}
        title="Attach Resolver"
        description={
          pendingAttachment
            ? "Choose which approved connection should back this resolver on the map."
            : "Choose a host-approved resolver for this layer. Resolver ids are shown as reference metadata only."
        }
        onOpenChange={open => {
          setPickerOpen(open)
          if (!open) {
            setPendingAttachment(null)
            setSearch("")
          }
        }}
        className="w-[calc(100vw-2rem)] max-w-3xl"
      >
        {pendingAttachment ? (
          <div className="grid gap-4">
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setPendingAttachment(null)}
                className="inline-flex items-center gap-1 rounded border border-border/70 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
              >
                <ChevronLeft className="h-3 w-3" />
                Back
              </button>
              <span className="text-[10px] font-medium text-muted-foreground">
                {pendingAttachment.compatibleConnections.length} approved connection{pendingAttachment.compatibleConnections.length === 1 ? "" : "s"}
              </span>
            </div>

            <div className="rounded-lg border border-border/60 bg-muted/10 px-4 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-bold text-foreground">{resolveResolverTitle(pendingAttachment.resolver)}</span>
                {pendingAttachment.resolver.provider ? (
                  <span className="rounded border border-border/70 bg-background px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                    {pendingAttachment.resolver.provider}
                  </span>
                ) : null}
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {pendingAttachment.resolver.description || "Choose the approved host connection for this resolver."}
              </p>
            </div>

            <ResolverSetupSummary resolver={pendingAttachment.resolver} />

            <div className="grid gap-3">
              <div className="rounded-lg border border-border bg-card px-4 py-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-bold text-foreground">Managed by host</span>
                      <StatusPill tone="neutral">Default</StatusPill>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Let the platform choose the default approved connection for this resolver at runtime.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => attachResolver(pendingAttachment.resolver, null, { mode: "managed" })}
                    className="inline-flex h-8 shrink-0 items-center gap-1 rounded border border-primary/20 bg-primary px-3 text-[10px] font-black uppercase tracking-wider text-primary-foreground transition-colors hover:bg-primary/90"
                  >
                    <BadgeCheck className="h-3 w-3" />
                    Use Default
                  </button>
                </div>
              </div>

              {pendingAttachment.compatibleConnections.map(connection => (
                <div key={connection.resolver_connection_id} className="rounded-lg border border-border bg-card px-4 py-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 space-y-2">
                      <ConnectionDetails connection={connection} fallbackStatus={connection.status} compact />
                      <p className="text-[10px] text-muted-foreground">
                        {describeConnectionFit(connection)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => attachResolver(pendingAttachment.resolver, connection)}
                      className="inline-flex h-8 shrink-0 items-center gap-1 rounded border border-primary/20 bg-primary px-3 text-[10px] font-black uppercase tracking-wider text-primary-foreground transition-colors hover:bg-primary/90"
                    >
                      <BadgeCheck className="h-3 w-3" />
                      Use Connection
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid gap-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
              <Input
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Search resolvers, providers, or integration ids"
                className="pl-9 text-sm"
              />
            </div>

            <div className="grid gap-3">
              {pickerEntries.map(({ resolver, identity, compatibleConnections, disabled, disabledReason, availability }) => (
                <div
                  key={identity}
                  className={cn(
                    "rounded-lg border px-4 py-3 transition-colors",
                    disabled ? "border-border/60 bg-muted/10" : "border-border bg-card hover:border-primary/30",
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-bold text-foreground">{resolveResolverTitle(resolver)}</span>
                        <span className={cn("rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider", resolveAvailabilityTone(availability).className)}>
                          {resolveAvailabilityTone(availability).label}
                        </span>
                        {resolver.provider ? (
                          <span className="rounded border border-border/70 bg-muted/30 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                            {resolver.provider}
                          </span>
                        ) : null}
                        <StatusPill tone={compatibleConnections.length > 1 ? "warning" : "neutral"}>
                          {compatibleConnections.length > 1
                            ? `${compatibleConnections.length} Connections`
                            : compatibleConnections.length === 1
                              ? "1 Connection"
                              : "Host Managed"}
                        </StatusPill>
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        {resolver.description || "Resolver metadata is available from the host catalog."}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 text-[10px] font-medium text-muted-foreground">
                        <span>Resolver: {resolver.resolver_type_id || resolver.resolver_id}</span>
                        <span>Attachment: {resolver.resolver_attachment_id || "Created on attach"}</span>
                        {resolver.resolver_connection_id ? (
                          <span>Connection: {resolver.resolver_connection_id}</span>
                        ) : (
                          <span>Connection: Managed by host</span>
                        )}
                        {resolver.required_inputs?.length ? (
                          <span>{resolver.required_inputs.length} required input{resolver.required_inputs.length === 1 ? "" : "s"}</span>
                        ) : null}
                      </div>
                      <ConnectionDetails
                        connection={connectionsById.get(resolver.resolver_connection_id ?? "") ?? null}
                        fallbackStatus={resolver.connection_status}
                        fallbackConnectionId={resolver.resolver_connection_id}
                        compact
                      />
                      {disabledReason ? (
                        <div className="flex items-center gap-2 text-[10px] font-medium text-muted-foreground">
                          <Unplug className="h-3 w-3" />
                          {disabledReason}
                        </div>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => beginResolverAttach(resolver, compatibleConnections)}
                      className={cn(
                        "inline-flex h-8 shrink-0 items-center gap-1 rounded border px-3 text-[10px] font-black uppercase tracking-wider transition-colors",
                        disabled
                          ? "cursor-not-allowed border-border/70 bg-muted/40 text-muted-foreground/50"
                          : "border-primary/20 bg-primary text-primary-foreground hover:bg-primary/90",
                      )}
                    >
                      <BadgeCheck className="h-3 w-3" />
                      {compatibleConnections.length > 1 ? "Choose" : "Attach"}
                    </button>
                  </div>
                </div>
              ))}

              {pickerEntries.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed bg-muted/10 p-8 text-center">
                  <Database className="mb-2 h-6 w-6 text-muted-foreground/30" />
                  <p className="text-sm font-semibold text-foreground">No approved resolvers available</p>
                  <p className="mt-1 max-w-md text-[11px] text-muted-foreground">
                    Resolver options must be projected by the host platform. This layer does not currently have any compatible resolver catalog entries.
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </MapInteractivityModalShell>
    </>
  )
}

function MetricRow({
  icon,
  label,
  value,
}: {
  icon: ReactNode
  label: string
  value: string
}) {
  return (
    <div className="rounded-lg border border-border/60 bg-muted/10 px-3 py-2">
      <div className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
        {icon}
        {label}
      </div>
      <div className="break-all text-[11px] font-semibold text-foreground">{value}</div>
    </div>
  )
}

function TokenGroup({
  icon,
  label,
  items,
  emptyLabel,
}: {
  icon: ReactNode
  label: string
  items: string[]
  emptyLabel: string
}) {
  return (
    <div className="grid gap-1.5">
      <Label className="flex items-center gap-1 text-[10px] font-bold uppercase text-muted-foreground/60">
        {icon}
        {label}
      </Label>
      <div className="flex min-h-10 flex-wrap items-center gap-1 rounded-lg border border-border/60 bg-muted/10 px-2 py-2">
        {items.length > 0 ? (
          items.map(item => (
            <span key={item} className="rounded bg-background px-1.5 py-0.5 text-[10px] font-bold text-foreground shadow-sm">
              {item}
            </span>
          ))
        ) : (
          <span className="text-[10px] text-muted-foreground">{emptyLabel}</span>
        )}
      </div>
    </div>
  )
}

function ResolverSetupSummary({
  resolver,
}: {
  resolver: LayerExperienceResolverDefinition
}) {
  const requiredInputs = resolver.required_inputs ?? []
  const requiredCapabilities = resolver.required_capabilities ?? []
  const guidance = describeResolverSetupGuidance(resolver)
  const rendererKind = resolver.renderer_kind ?? resolver.default_renderer_kind ?? null

  return (
    <div className="grid gap-2 rounded-lg border border-border/60 bg-muted/10 px-4 py-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">Setup Guidance</div>
      <p className="text-[11px] text-muted-foreground">{guidance}</p>
      <div className="flex flex-wrap items-center gap-3 text-[10px] font-medium text-muted-foreground">
        <span>{requiredInputs.length} required input{requiredInputs.length === 1 ? "" : "s"}</span>
        <span>{requiredCapabilities.length} required {requiredCapabilities.length === 1 ? "capability" : "capabilities"}</span>
        {resolver.renderer_kind ? <span>Renderer: {RENDERER_LABELS[resolver.renderer_kind]}</span> : null}
      </div>
      {requiredCapabilities.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1">
          {requiredCapabilities.map(capability => (
            <span
              key={capability}
              className="rounded bg-background px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground shadow-sm"
            >
              {formatTokenLabel(capability)}
            </span>
          ))}
        </div>
      ) : null}
      {rendererKind ? <ResolverRendererPreview rendererKind={rendererKind} /> : null}
    </div>
  )
}

function ResolverRendererPreview({
  rendererKind,
}: {
  rendererKind: LayerExperienceResolverRendererKind
}) {
  return (
    <div className="grid gap-2 rounded-lg border border-border/60 bg-background px-3 py-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">Renderer Preview</span>
        <StatusPill tone="neutral">{RENDERER_LABELS[rendererKind]}</StatusPill>
      </div>
      {rendererKind === "notes_timeline" ? (
        <div className="grid gap-2">
          <div className="flex gap-2">
            <div className="mt-1 h-2 w-2 rounded-full bg-primary" />
            <div className="min-w-0">
              <div className="text-[11px] font-semibold text-foreground">Newest note</div>
              <div className="text-[10px] text-muted-foreground">Technician updated arrival and customer contact.</div>
            </div>
          </div>
          <div className="flex gap-2 opacity-70">
            <div className="mt-1 h-2 w-2 rounded-full bg-muted-foreground/40" />
            <div className="min-w-0">
              <div className="text-[11px] font-semibold text-foreground">Previous note</div>
              <div className="text-[10px] text-muted-foreground">Dispatch created the service ticket.</div>
            </div>
          </div>
        </div>
      ) : null}
      {rendererKind === "address_details" ? (
        <div className="grid gap-2">
          <div className="text-[11px] font-semibold text-foreground">123 Main St, Detroit, MI</div>
          <div className="grid gap-1 text-[10px] text-muted-foreground">
            <div className="flex items-center justify-between gap-2">
              <span>Place ID</span>
              <span className="font-medium text-foreground/80">Resolved</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span>Coordinates</span>
              <span className="font-medium text-foreground/80">42.3314, -83.0458</span>
            </div>
          </div>
        </div>
      ) : null}
      {rendererKind === "record_summary" ? (
        <div className="grid gap-2">
          <div className="text-[11px] font-semibold text-foreground">Service Ticket Summary</div>
          <div className="grid gap-1 text-[10px] text-muted-foreground">
            <div className="flex items-center justify-between gap-2">
              <span>Status</span>
              <span className="font-medium text-foreground/80">Ready</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span>Priority</span>
              <span className="font-medium text-foreground/80">High</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span>Owner</span>
              <span className="font-medium text-foreground/80">Field Ops</span>
            </div>
          </div>
        </div>
      ) : null}
      {rendererKind === "activity_feed" ? (
        <div className="grid gap-2">
          <div className="rounded border border-border/60 bg-muted/10 px-2 py-1.5 text-[10px] text-muted-foreground">
            10:42 AM · Ticket assigned to technician
          </div>
          <div className="rounded border border-border/60 bg-muted/10 px-2 py-1.5 text-[10px] text-muted-foreground">
            11:08 AM · Customer notified of arrival window
          </div>
        </div>
      ) : null}
      {rendererKind === "related_items_table" ? (
        <div className="grid gap-1 rounded border border-border/60 bg-muted/10 px-2 py-2 text-[10px]">
          <div className="grid grid-cols-[1.4fr,0.8fr,0.8fr] gap-2 font-bold uppercase tracking-wider text-muted-foreground/70">
            <span>Item</span>
            <span>Status</span>
            <span>Type</span>
          </div>
          <div className="grid grid-cols-[1.4fr,0.8fr,0.8fr] gap-2 text-muted-foreground">
            <span className="text-foreground/90">Ticket 10482</span>
            <span>Open</span>
            <span>Service</span>
          </div>
          <div className="grid grid-cols-[1.4fr,0.8fr,0.8fr] gap-2 text-muted-foreground">
            <span className="text-foreground/90">Work Order 388</span>
            <span>Ready</span>
            <span>Dispatch</span>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function ConnectionDetails({
  connection,
  fallbackStatus,
  fallbackConnectionId,
  compact = false,
}: {
  connection: LayerExperienceResolverConnectionDefinition | null
  fallbackStatus?: LayerExperienceResolverDefinition["connection_status"] | null
  fallbackConnectionId?: string | null
  compact?: boolean
}) {
  const title = connection ? resolveConnectionTitle(connection) : fallbackConnectionId || "Managed by host"
  const status = connection?.status ?? fallbackStatus ?? null
  const scopeLabel = connection ? formatConnectionScope(connection) : null
  const hasMetadata = Boolean(connection)
  const capabilities = connection?.required_capabilities ?? []

  return (
    <div className={cn("rounded-lg border border-border/60 bg-muted/10", compact ? "px-3 py-2" : "px-3 py-2.5")}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold text-foreground">{title}</span>
        <StatusPill tone={resolveConnectionStatusTone(status)}>{formatConnectionStatus(status)}</StatusPill>
        {scopeLabel ? <StatusPill tone="neutral">{scopeLabel}</StatusPill> : null}
      </div>
      {hasMetadata ? (
        <div className="mt-1 space-y-1">
          {connection?.scope_id ? (
            <div className="text-[10px] text-muted-foreground">
              Scope ID: <span className="font-medium text-foreground/80">{connection.scope_id}</span>
            </div>
          ) : null}
          {capabilities.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1">
              {capabilities.map(capability => (
                <span
                  key={capability}
                  className="rounded bg-background px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground shadow-sm"
                >
                  {formatTokenLabel(capability)}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="mt-1 text-[10px] text-muted-foreground">
          The platform selects the default approved connection at runtime.
        </div>
      )}
    </div>
  )
}

function StatusPill({
  tone,
  children,
}: {
  tone: "positive" | "warning" | "danger" | "neutral"
  children: ReactNode
}) {
  const className =
    tone === "positive"
      ? "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20"
      : tone === "warning"
        ? "bg-amber-500/10 text-amber-700 ring-amber-500/20"
        : tone === "danger"
          ? "bg-rose-500/10 text-rose-700 ring-rose-500/20"
          : "bg-background text-muted-foreground ring-border/70"
  return (
    <span className={cn("rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ring-1", className)}>
      {children}
    </span>
  )
}

function resolveResolverIdentity(resolver: LayerExperienceResolverDefinition): string {
  return resolver.resolver_attachment_id || resolver.resolver_id
}

function resolveResolverTitle(resolver: LayerExperienceResolverDefinition): string {
  return resolver.label || resolver.description || resolver.resolver_type_id || resolver.resolver_id
}

function matchesResolverContext(
  resolver: LayerExperienceResolverDefinition,
  currentSourceId: string | null,
  currentSourceLayer: string | null,
): boolean {
  const sourceMatches = !resolver.source_id || !currentSourceId || resolver.source_id === currentSourceId
  const layerMatches = !resolver.source_layer || !currentSourceLayer || resolver.source_layer === currentSourceLayer
  return sourceMatches && layerMatches
}

function buildResolverAttachmentId(
  template: LayerExperienceResolverDefinition,
  existingResolvers: LayerExperienceResolverDefinition[],
): string {
  const existingIds = new Set(
    existingResolvers
      .map(entry => entry.resolver_attachment_id)
      .filter((entry): entry is string => Boolean(entry && entry.trim())),
  )
  const base =
    (template.resolver_attachment_id || template.resolver_type_id || template.resolver_id || "resolver")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "resolver"
  let nextId = `${base}-attachment`
  let counter = 2
  while (existingIds.has(nextId)) {
    nextId = `${base}-attachment-${counter}`
    counter += 1
  }
  return nextId
}

function resolveAvailabilityTone(availability: LayerExperienceResolverAvailability) {
  switch (availability) {
    case "available":
      return { label: "Available", className: "bg-emerald-500/10 text-emerald-600 ring-1 ring-emerald-500/20" }
    case "tenant_not_entitled":
      return { label: "Entitlement", className: "bg-amber-500/10 text-amber-700 ring-1 ring-amber-500/20" }
    case "connection_missing":
    case "connection_not_ready":
      return { label: "Connection", className: "bg-orange-500/10 text-orange-700 ring-1 ring-orange-500/20" }
    case "user_forbidden":
      return { label: "Restricted", className: "bg-rose-500/10 text-rose-700 ring-1 ring-rose-500/20" }
    case "layer_incompatible":
      return { label: "Layer Mismatch", className: "bg-slate-500/10 text-slate-700 ring-1 ring-slate-500/20" }
    case "platform_disabled":
      return { label: "Disabled", className: "bg-muted text-muted-foreground ring-1 ring-border/70" }
    default:
      return { label: "Unknown", className: "bg-muted text-muted-foreground ring-1 ring-border/70" }
  }
}

function formatAvailabilityReason(availability: LayerExperienceResolverAvailability): string {
  switch (availability) {
    case "tenant_not_entitled":
      return "This resolver is not enabled for the current tenant or organization."
    case "connection_missing":
      return "A required connection has not been configured."
    case "connection_not_ready":
      return "The required connection exists but is not ready."
    case "user_forbidden":
      return "The current user is not allowed to attach this resolver."
    case "layer_incompatible":
      return "This resolver is not compatible with the selected source layer."
    case "platform_disabled":
      return "This resolver is currently disabled by the platform."
    default:
      return "This resolver is not currently available."
  }
}

function formatConnectionStatus(status?: LayerExperienceResolverDefinition["connection_status"] | null): string {
  switch (status) {
    case "ready":
      return "Ready"
    case "needs_configuration":
      return "Needs Configuration"
    case "disabled":
      return "Disabled"
    case "invalid":
      return "Invalid"
    default:
      return "Managed By Host"
  }
}

function resolveConnectionStatusTone(
  status?: LayerExperienceResolverDefinition["connection_status"] | null,
): "positive" | "warning" | "danger" | "neutral" {
  switch (status) {
    case "ready":
      return "positive"
    case "needs_configuration":
      return "warning"
    case "disabled":
    case "invalid":
      return "danger"
    default:
      return "neutral"
  }
}

function resolveCompatibleResolverConnections(
  resolver: LayerExperienceResolverDefinition,
  connections: LayerExperienceResolverConnectionDefinition[],
): LayerExperienceResolverConnectionDefinition[] {
  return connections.filter(connection => {
    if (resolver.resolver_connection_id && connection.resolver_connection_id === resolver.resolver_connection_id) {
      return true
    }
    if (!resolver.provider || !connection.provider) {
      return true
    }
    return resolver.provider === connection.provider
  })
}

function resolveConnectionTitle(connection: LayerExperienceResolverConnectionDefinition): string {
  return connection.label || connection.resolver_connection_id
}

function formatConnectionScope(connection: LayerExperienceResolverConnectionDefinition): string {
  switch (connection.scope_kind) {
    case "tenant":
      return "Tenant Scope"
    case "organization":
      return "Organization Scope"
    case "user":
      return "User Scope"
    case "global":
      return "Global Scope"
    default:
      return connection.scope_kind ? `${formatTokenLabel(connection.scope_kind)} Scope` : "Host Scope"
  }
}

function describeConnectionFit(connection: LayerExperienceResolverConnectionDefinition): string {
  switch (connection.scope_kind) {
    case "tenant":
      return "Best when this tenant needs dedicated credentials or tenant-specific data boundaries."
    case "organization":
      return "Best when several tenants in the same organization should share the same integration setup."
    case "user":
      return "Best when a resolver must run with a user-specific credential or delegated identity."
    case "global":
      return "Best when the platform should share one centrally managed integration across every tenant."
    default:
      return "Use when this connection is the approved host configuration for the resolver."
  }
}

function describeResolverSetupGuidance(resolver: LayerExperienceResolverDefinition): string {
  const provider = resolver.provider ? formatTokenLabel(resolver.provider) : "host"
  const requiredCapabilities = resolver.required_capabilities ?? []
  if (requiredCapabilities.length > 0) {
    return `${provider} access must already be configured for the selected connection. Choose the scope that matches where those credentials should live.`
  }
  return `Choose the connection scope that matches who should own and maintain this ${provider.toLowerCase()} integration.`
}

function formatResolverConnectionOptionLabel(connection: LayerExperienceResolverConnectionDefinition): string {
  return `${resolveConnectionTitle(connection)} (${formatConnectionScope(connection)} · ${formatConnectionStatus(connection.status)})`
}

function formatResolverConnectionSummary(
  connection: LayerExperienceResolverConnectionDefinition | null,
  resolver: LayerExperienceResolverDefinition,
): string {
  if (connection) {
    return formatResolverConnectionOptionLabel(connection)
  }
  if (resolver.resolver_connection_id) {
    return `${resolver.resolver_connection_id} (${formatConnectionStatus(resolver.connection_status)})`
  }
  return formatConnectionStatus(resolver.connection_status)
}

function formatTokenLabel(value: string): string {
  return value
    .split("_")
    .map(token => token.charAt(0).toUpperCase() + token.slice(1))
    .join(" ")
}
